import { isOneOf } from "@remi/ts-utils";
import {
  type PresenceData,
  PresenceDataSchema,
  PresenceEntityType,
  PresenceEntityTypeValues,
  type PresenceKey,
  type PresenceMap,
} from "@remi/types/socket/presence";

import { resolveInboxItem } from "~/hooks/inbox/resolveInboxItem";

type PresenceEntityTypeValue = (typeof PresenceEntityTypeValues)[number];

export const serializePresenceKey = (key: PresenceKey): string => `${key.entityType}:${key.entityId}`;

export const getPresenceChannelName = (entityType: PresenceEntityTypeValue): string => {
  return `presence:presence-${entityType.toLowerCase()}`;
};

export const groupSerializedPresenceKeysByEntityType = (
  keys: PresenceKey[] | null,
): Partial<Record<PresenceEntityTypeValue, string[]>> => {
  const grouped = (keys ?? []).reduce<Partial<Record<PresenceEntityTypeValue, Set<string>>>>((acc, key) => {
    const currentSet = acc[key.entityType] ?? new Set<string>();
    currentSet.add(serializePresenceKey(key));
    acc[key.entityType] = currentSet;
    return acc;
  }, {});

  const groupedKeysByEntityType: Partial<Record<PresenceEntityTypeValue, string[]>> = {};
  for (const entityType of PresenceEntityTypeValues) {
    const keySet = grouped[entityType];
    if (!keySet) {
      continue;
    }
    groupedKeysByEntityType[entityType] = [...keySet].sort();
  }

  return groupedKeysByEntityType;
};

const getRouteParam = (value: string | string[] | undefined) => (Array.isArray(value) ? value[0] : value);

export const getRfqIdFromRoute = (
  pathname: string,
  query: Record<string, string | string[] | undefined>,
): number | null => {
  if (!pathname.startsWith("/quoting")) {
    return null;
  }
  const raw = getRouteParam(query.rfqId);
  const rfqId = Number(raw);
  return raw && Number.isFinite(rfqId) ? rfqId : null;
};

export const parsePresenceKey = (value: string): PresenceKey | null => {
  const separatorIndex = value.indexOf(":");
  if (separatorIndex <= 0) {
    return null;
  }
  const entityType = value.slice(0, separatorIndex);
  const entityId = value.slice(separatorIndex + 1);
  if (!entityId || !isOneOf(entityType, PresenceEntityTypeValues)) {
    return null;
  }
  return { entityType, entityId };
};

export const getPresenceKeysFromRoute = (
  pathname: string,
  query: Record<string, string | string[] | undefined>,
  rfqToTaskMap: Map<number, string | undefined>,
): PresenceKey[] => {
  const conversationId = getRouteParam(query.conversationId);
  const taskId = getRouteParam(query.taskId);
  const bidId = getRouteParam(query.bidId);
  const projectId = getRouteParam(query.projectId);
  const keys: PresenceKey[] = [];

  if (pathname.startsWith("/inbox")) {
    const inboxItem = resolveInboxItem(query);
    if (inboxItem?.type === "ticket") {
      keys.push({ entityType: PresenceEntityType.TICKET, entityId: inboxItem.id });
    } else if (inboxItem?.type === "conversation") {
      keys.push({ entityType: PresenceEntityType.CONVERSATION, entityId: inboxItem.id });
    } else if (inboxItem?.type === "task") {
      keys.push({ entityType: PresenceEntityType.TASK, entityId: inboxItem.id });
    }
  }

  if (pathname.startsWith("/conversations") && conversationId) {
    keys.push({ entityType: PresenceEntityType.CONVERSATION, entityId: conversationId });
  }

  if ((pathname.startsWith("/dashboard/bids") || pathname.startsWith("/admin/bids")) && bidId) {
    keys.push({ entityType: PresenceEntityType.BID, entityId: String(bidId) });
  }

  if (pathname.startsWith("/dashboard/tasks") && taskId) {
    keys.push({ entityType: PresenceEntityType.TASK, entityId: taskId });
  }

  if (pathname.startsWith("/dashboard/") && projectId) {
    keys.push({ entityType: PresenceEntityType.PROJECT, entityId: String(projectId) });
  }

  if (pathname.startsWith("/live-photo-review")) {
    const submissionId = getRouteParam(query.submissionId);
    const questionId = getRouteParam(query.questionId);
    if (submissionId) {
      keys.push({
        entityType: PresenceEntityType.CHECKLIST_QUESTION_REVIEW,
        entityId: submissionId,
      });
    }
    if (submissionId && questionId) {
      keys.push({
        entityType: PresenceEntityType.CHECKLIST_QUESTION_REVIEW,
        entityId: `${submissionId}-${questionId}`,
      });
    }
  }

  const rfqId = getRfqIdFromRoute(pathname, query);
  if (rfqId !== null) {
    const createQuoteTaskId = rfqToTaskMap.get(rfqId);
    if (createQuoteTaskId) {
      keys.push({ entityType: PresenceEntityType.TASK, entityId: createQuoteTaskId });
    }
  }

  return keys;
};

export const parsePresenceData = (data: unknown): PresenceData | null => {
  const parsed = PresenceDataSchema.safeParse(data);
  return parsed.success ? parsed.data : null;
};

// Ably clientId is the personId (WsContext), so a user's tabs share it; presence must
// key on connectionId or a sibling tab's leave wipes another tab's keys for the same user.
type PresenceMember = { clientId: string; serializedKeys: string[] };
export type EntityPresenceMembers = Record<string, PresenceMember>;

const serializedKeysEqual = (a: string[], b: string[]): boolean => {
  if (a.length !== b.length) {
    return false;
  }
  const counts = new Map<string, number>();
  for (const key of a) {
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }
  for (const key of b) {
    const remaining = counts.get(key);
    if (!remaining) {
      return false;
    }
    counts.set(key, remaining - 1);
  }
  return true;
};

export const buildPresenceMapFromMembers = (members: EntityPresenceMembers): PresenceMap => {
  const presenceMap: PresenceMap = {};
  for (const { clientId, serializedKeys } of Object.values(members)) {
    for (const serializedKey of serializedKeys) {
      const existingClientIds = presenceMap[serializedKey] ?? [];
      if (!existingClientIds.includes(clientId)) {
        presenceMap[serializedKey] = [...existingClientIds, clientId];
      }
    }
  }
  return presenceMap;
};

export const applyPresenceMemberUpdate = (
  members: EntityPresenceMembers,
  connectionId: string,
  clientId: string,
  serializedKeys: string[],
): EntityPresenceMembers => {
  const existingMember = members[connectionId];

  if (serializedKeys.length === 0) {
    if (!existingMember) {
      return members;
    }
    const nextMembers = { ...members };
    delete nextMembers[connectionId];
    return nextMembers;
  }

  if (
    existingMember &&
    existingMember.clientId === clientId &&
    serializedKeysEqual(existingMember.serializedKeys, serializedKeys)
  ) {
    return members;
  }

  return { ...members, [connectionId]: { clientId, serializedKeys } };
};
